import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  FiSearch,
  FiChevronDown,
  FiCheck,
  FiPrinter,
  FiUser,
  FiShield,
  FiX,
  FiSliders,
  FiRefreshCw,
  FiAlertCircle,
  FiPlus,
} from "react-icons/fi";

import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";

// Modal Component
import CreateStudentPassModal from "../modal/CreateStudentPassModal";

// --- CUSTOM PORTAL DROPDOWN COMPONENT ---
function CustomDropdown({ label, value, options, onChange, icon: Icon, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160),
      });
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[9999] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  String(value) === String(option.value)
                    ? "bg-zinc-100 text-zinc-900 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {String(value) === String(option.value) && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function StudentPassTab() {
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- DATA STATES ---
  const [passCards, setPassCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  // --- SELECTION & PREVIEW ---
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [activePreviewId, setActivePreviewId] = useState(null);

  // --- FILTERS & PAGINATION ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPassTypeFilter, setSelectedPassTypeFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  // --- CARD CUSTOMIZATION TOGGLES ---
  const [showQrCode, setShowQrCode] = useState(true);
  const [showEmergencyContact, setShowEmergencyContact] = useState(true);

  // Pass Type Options matching backend PassType enum
  const passTypeFilterOptions = useMemo(
    () => [
      { value: "ALL", label: "All Pass Types" },
      { value: "PARENT", label: "Parent Pickup" },
      { value: "SELF", label: "Self Dismissal" },
      { value: "RIDE_HAILING", label: "Ride Hailing" },
    ],
    []
  );

  const passTypeEditOptions = useMemo(
    () => [
      { value: "PARENT", label: "Parent Pickup" },
      { value: "SELF", label: "Self Dismissal" },
      { value: "RIDE_HAILING", label: "Ride Hailing" },
    ],
    []
  );

  // --- FETCH PASS CARDS FROM API ---
  const fetchPassCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (selectedPassTypeFilter !== "ALL") params.append("pass_type", selectedPassTypeFilter);
      params.append("page", currentPage);
      params.append("page_size", pageSize);

      const response = await api.get(`/student-pass-card/?${params.toString()}`);
      const data = response.data !== undefined ? response.data : await response.json();

      if (data && Array.isArray(data.results)) {
        setPassCards(data.results);
        setTotalCards(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize) || 1);
      } else if (Array.isArray(data)) {
        setPassCards(data);
        setTotalCards(data.length);
        setTotalPages(1);
      } else {
        setPassCards([]);
        setTotalCards(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error loading pass cards:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to load student pass cards."
      );
      setPassCards([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedPassTypeFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchPassCards();
  }, [fetchPassCards]);

  // --- SELECTION HANDLERS ---
  const toggleSelectCard = (id) => {
    setSelectedCardIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCardIds.length === passCards.length && passCards.length > 0) {
      setSelectedCardIds([]);
    } else {
      setSelectedCardIds(passCards.map((c) => c.id));
    }
  };

  // Currently Active Preview Pass Card
  const previewCard =
    passCards.find((c) => c.id === activePreviewId) || passCards[0] || null;

  // Handle updating pass_type via API
  const handlePassTypeChange = async (newPassType) => {
    if (!previewCard) return;
    setUpdating(true);
    try {
      const response = await api.post("/student-pass-card/", {
        student: previewCard.student,
        pass_type: newPassType,
      });

      // Refresh list to show updated student_id_number & pass_type_display
      await fetchPassCards();
      setActivePreviewId(response.data?.id || previewCard.id);
    } catch (err) {
      console.error("Error updating pass type:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4 relative">
      {/* FILTER & TOP ACTION BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-1">
        <div className="flex flex-wrap flex-1 items-center gap-2 max-w-xl">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by student name or ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Pass Type Filter Dropdown */}
          <div className="relative flex-1 min-w-[150px]">
            <CustomDropdown
              label="Pass Type"
              value={selectedPassTypeFilter}
              options={passTypeFilterOptions}
              onChange={(val) => {
                setSelectedPassTypeFilter(val);
                setCurrentPage(1);
              }}
              icon={FiShield}
            />
          </div>
        </div>

        {/* Create Modal & Print Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors cursor-pointer"
          >
            <FiPlus className="w-3.5 h-3.5" />
            <span>Create Pass Card</span>
          </button>

          <button
            type="button"
            onClick={handlePrint}
            disabled={!previewCard && selectedCardIds.length === 0}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors shadow-xs disabled:opacity-50 cursor-pointer"
          >
            <FiPrinter className="w-3.5 h-3.5" />
            <span>
              Print Pass ({selectedCardIds.length > 0 ? selectedCardIds.length : 1})
            </span>
          </button>
        </div>
      </div>

      {/* MAIN TWO-COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT COLUMN: Student Pass Card Table */}
        <div className="lg:col-span-7 bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden flex flex-col justify-between">
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
                  <th className="py-3 px-4 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={
                        passCards.length > 0 &&
                        selectedCardIds.length === passCards.length
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-400 cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-4">Student</th>
                  <th className="py-3 px-4">Pass ID Number</th>
                  <th className="py-3 px-4">Pass Type</th>
                  <th className="py-3 px-4 text-right">Preview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200/60 text-xs text-zinc-700">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-zinc-400">
                      <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                      Loading pass cards...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-red-500">
                      <FiAlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
                      {error}
                    </td>
                  </tr>
                ) : passCards.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-zinc-400">
                      No pass cards found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  passCards.map((card) => {
                    const isSelected = selectedCardIds.includes(card.id);
                    const isPreviewing = previewCard?.id === card.id;

                    return (
                      <tr
                        key={card.id}
                        onClick={() => setActivePreviewId(card.id)}
                        className={`hover:bg-zinc-50/80 transition-colors cursor-pointer ${
                          isPreviewing ? "bg-zinc-100/70" : ""
                        }`}
                      >
                        <td
                          className="py-3 px-4 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectCard(card.id)}
                            className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-400 cursor-pointer"
                          />
                        </td>

                        <td className="py-3 px-4 font-semibold text-zinc-900">
                          {card.student_name || `${card.student_last_name}, ${card.student_first_name}`}
                        </td>

                        <td className="py-3 px-4 font-mono text-[11px] text-zinc-500">
                          {card.student_id_number || "—"}
                        </td>

                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                            {card.pass_type_display || card.pass_type}
                          </span>
                        </td>

                        <td className="py-3 px-4 text-right">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${
                              isPreviewing
                                ? "bg-zinc-900 text-white border-zinc-900"
                                : "bg-zinc-100 text-zinc-600 border-zinc-200 hover:bg-zinc-200"
                            }`}
                          >
                            {isPreviewing ? "Active" : "Select"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {!loading && !error && passCards.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCards}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {/* RIGHT COLUMN: Settings & Live Card Preview */}
        <div className="lg:col-span-5 space-y-4">
          {/* Customization Settings */}
          <div className="bg-white border border-zinc-200/80 rounded-lg p-4 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
              <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                <FiSliders className="w-3.5 h-3.5 text-zinc-500" />
                Pass Customization
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase mb-1">
                  Pass Type
                </label>
                <CustomDropdown
                  value={previewCard?.pass_type || "PARENT"}
                  options={passTypeEditOptions}
                  onChange={handlePassTypeChange}
                  disabled={!previewCard || updating}
                  icon={FiShield}
                />
              </div>

              <div className="flex flex-col justify-end space-y-2 pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-zinc-700">
                  <input
                    type="checkbox"
                    checked={showQrCode}
                    onChange={(e) => setShowQrCode(e.target.checked)}
                    className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-400"
                  />
                  <span>Show QR Code</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-zinc-700">
                  <input
                    type="checkbox"
                    checked={showEmergencyContact}
                    onChange={(e) => setShowEmergencyContact(e.target.checked)}
                    className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-400"
                  />
                  <span>Emergency Contact</span>
                </label>
              </div>
            </div>
          </div>

          {/* Live Card Preview Box */}
          <div className="bg-white border border-zinc-200/80 rounded-lg p-5 shadow-xs flex flex-col items-center justify-center">
            <div className="w-full flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Printable Live Preview
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200 uppercase">
                {previewCard?.pass_type_display || "PASS CARD"}
              </span>
            </div>

            {previewCard ? (
              /* Printable CR80 Standard Pass Card */
              <div
                id="printable-card"
                style={{ backgroundColor: "#002855" }}
                className="w-72 h-[420px] text-white rounded-xl shadow-lg p-5 flex flex-col justify-between border border-blue-900/40 relative overflow-hidden"
              >
                {/* Subtle Background Geometric Accent */}
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/5 rounded-full blur-xl pointer-events-none" />

                {/* Header */}
                <div className="text-center space-y-0.5 border-b border-white/20 pb-3 z-10">
                  <h4 className="text-[11px] font-extrabold uppercase tracking-widest text-white">
                    ACADEMIC INSTITUTION
                  </h4>
                  <p className="text-[9px] font-semibold text-blue-200 tracking-wider uppercase">
                    OFFICIAL STUDENT PASS
                  </p>
                </div>

                {/* Avatar / Photo */}
                <div className="flex flex-col items-center my-auto space-y-2.5 z-10">
                  <div className="w-24 h-24 rounded-full border-2 border-white/30 bg-white/10 flex items-center justify-center overflow-hidden shadow-md backdrop-blur-xs">
                    {previewCard.student_photo ? (
                      <img
                        src={previewCard.student_photo}
                        alt="Student"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FiUser className="w-10 h-10 text-blue-200" />
                    )}
                  </div>
                  <div className="text-center">
                    <h5 className="text-sm font-bold text-white tracking-wide">
                      {previewCard.student_last_name}, {previewCard.student_first_name}
                    </h5>
                    <p className="text-[11px] text-blue-200 font-medium mt-0.5">
                      {previewCard.pass_type_display || "Official Pass"}
                    </p>
                  </div>
                </div>

                {/* Card Footer Details */}
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 space-y-2 z-10 border border-white/15">
                  <div className="flex justify-between items-center text-[10px] text-blue-100">
                    <span className="font-medium text-blue-200">Pass Code:</span>
                    <span className="font-mono font-bold text-white">
                      {previewCard.student_id_number || "N/A"}
                    </span>
                  </div>

                  {showEmergencyContact && (
                    <div className="flex justify-between items-center text-[10px] text-blue-100">
                      <span className="font-medium text-blue-200">Emergency:</span>
                      <span className="font-mono text-white">School Office</span>
                    </div>
                  )}

                  {showQrCode && (
                    <div className="pt-2 border-t border-white/15 flex justify-center">
                      <div className="bg-white p-1.5 rounded-md shadow-xs">
                        <QRCodeSVG
                          value={previewCard.student_id_number || "NO_ID"}
                          size={54}
                          fgColor="#002855"
                          bgColor="#FFFFFF"
                          level="M"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="w-72 h-[420px] border border-dashed border-zinc-200 rounded-xl flex flex-col items-center justify-center text-zinc-400 p-6 text-center">
                <FiUser className="w-10 h-10 mb-2 text-zinc-300" />
                <p className="text-xs font-medium">
                  Select a pass card from the list to render preview.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CREATE STUDENT PASS MODAL */}
      <CreateStudentPassModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaveSuccess={() => fetchPassCards()}
      />
    </div>
  );
}